import { IDashboardData, IChartSelectEvent } from '@models/Dashboard';
import { ChartJSService } from '@services/chartjs.service';
import { Component, Input, Output, EventEmitter, OnInit, ViewChild, ElementRef, AfterViewInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';

@Component({
	selector: 'pie-chart',
	templateUrl: './pie-chart.component.html',
	styleUrls: ['./pie-chart.component.scss'],
})
export class PieChartComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

	@Input('height') Height: string = "400";

	@ViewChild('canvasChart') canvasChart: ElementRef<HTMLCanvasElement>;

	@Input() data: IDashboardData;

	/** Emitido ao clicar numa fatia (drill-in). */
	@Output() select: EventEmitter<IChartSelectEvent> = new EventEmitter<IChartSelectEvent>();

	private chart: any;

	/** Conteúdo do último `data` desenhado — usado para não redesenhar o que não mudou. */
	private lastSignature: string = null;

	constructor(private ChartJSService: ChartJSService) { }

	ngOnInit() { }

	ngOnDestroy(): void {

		if (this.chart)
			this.chart.destroy();
	}

	ngAfterViewInit(): void {

		// Adiado um tick: garante que o container já tem tamanho quando o Chart.js mede
		// (senão, no primeiro render após revelar o segmento, o gráfico fica em branco).
		setTimeout(() => { if (this.canvasChart?.nativeElement) this.normalizeData(); });
	}

	/**
	 * O Chart.js não observa o array de dados: quando o `[data]` é substituído (ex.: nova
	 * pesquisa por datas) é preciso destruir e recriar o gráfico, senão o canvas mantém
	 * a série anterior. A primeira mudança é ignorada — quem cria é o ngAfterViewInit.
	 */
	ngOnChanges(changes: SimpleChanges): void {

		if (!changes['data'] || changes['data'].firstChange) return;
		if (!this.canvasChart?.nativeElement) return;

		// Compara o CONTEÚDO, não a referência: há bindings que criam um objeto novo a cada
		// ciclo de deteção de mudanças (ex.: [data]="{IdType:…, labels:[], datasets:[…]}" inline),
		// e isso não deve provocar um redesenho por ciclo.
		const signature = this.buildSignature(this.data);
		if (signature !== null && signature === this.lastSignature) return;

		this.rebuild();
	}

	/** Assinatura do conteúdo desenhado (fatias + séries). `null` = não comparável → redesenha. */
	private buildSignature(data: IDashboardData): string {

		try {
			return JSON.stringify({
				labels: data?.labels ?? [],
				datasets: (data?.datasets ?? []).map((s: any) => [s?.label, s?.data]),
			});
		}
		catch { return null; }
	}

	/** Destrói o gráfico atual (se existir) e volta a desenhá-lo com o `data` corrente. */
	private rebuild() {

		if (this.chart) {
			this.chart.destroy();
			this.chart = null;
		}

		this.normalizeData();
	}

	private async normalizeData(data: any = this.data) {

		this.lastSignature = this.buildSignature(data);

		this.chart = await this.ChartJSService.GetDoughnutChart(this.canvasChart.nativeElement, data?.labels, data?.datasets);

		this.attachClickHandler();
	}

	/** Liga o clique no canvas ao output `select` (fatia sob o cursor). */
	private attachClickHandler() {

		if (!this.chart || !this.canvasChart?.nativeElement) return;

		this.canvasChart.nativeElement.onclick = (evt: MouseEvent) => {

			const points = this.chart.getElementsAtEventForMode(evt, 'nearest', { intersect: true }, true);
			if (!points?.length) return;

			const { datasetIndex, index } = points[0];
			const ds = this.chart.data.datasets[datasetIndex];

			this.select.emit({
				label: this.chart.data.labels?.[index],
				value: ds?.data?.[index],
				datasetLabel: ds?.label,
				index,
				datasetIndex,
			});
		};
	}

}
